<template>
  <UserSelectMenu
    v-if="menuMode"
    ref="userComponent"
    v-model="selectedUsers"
    :user-list="assignableUsers"
    v-bind="$attrs"
  />
  <UserSelectDropdown
    v-else-if="dropdownMode"
    ref="userComponent"
    v-model="selectedUsers"
    :multiple="multiple"
    :user-list="assignableUsers"
    v-bind="$attrs"
  >
    <template #activator="{ attrs, on }">
      <slot
        name="activator"
        v-bind="{ attrs, on }"
      />
    </template>
  </UserSelectDropdown>
  <UserSelectAutocomplete
    v-else
    ref="userComponent"
    v-model="selectedUsers"
    :multiple="multiple"
    :user-list="assignableUsers"
    v-bind="$attrs"
  >
    <template
      #selection="data"
    >
      <slot
        name="selection"
        v-bind="data"
      />
    </template>
  </UserSelectAutocomplete>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { sortByKeyCaseInsensitive } from '@/utilities'

import UserSelectAutocomplete from './UserSelectAutocomplete.vue'
import UserSelectDropdown from './UserSelectDropdown.vue'
import UserSelectMenu from './UserSelectMenu.vue'

export default {
  name: 'UserSelect',

  components: {
    UserSelectAutocomplete,
    UserSelectDropdown,
    UserSelectMenu,
  },

  inheritAttrs: false,

  model: {
    prop: 'users',
    event: 'change',
  },

  props: {
    allowedUsers: {
      type: Array,
      default: undefined,
    },

    excludeUsers: {
      type: Array,
      default: _ => ([]),
    },

    extraOptions: {
      type: Array,
      default: _ => ([]),
    },

    hideDisabled: {
      type: Boolean,
      default: false,
    },

    mode: {
      type: String,
      default: 'autocomplete',
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    onlyAssignable: {
      type: Boolean,
      default: false,
    },

    onlyAttorneys: {
      type: Boolean,
      default: false,
    },

    teams: {
      type: Array,
      default: _ => ([]),
    },

    userTypes: {
      type: Array,
      default: _ => ([]),
    },

    users: {
      type: [Number, Array],
      default: _ => [],
    },
  },

  data () {
    return {
      lazyUsers: undefined,
      searchInput: '',
    }
  },

  computed: {
    assignableUsers () {
      let userList = this.usersOptions({
        considerTeam: !!this.teams.length,
        excludeIds: this.excludeUsers,
        includeAll: !this.hideDisabled,
        includeIds: Array.isArray(this.lazyUsers) ? this.lazyUsers : [this.lazyUsers],
        isAssignable: this.onlyAssignable,
        onlyAttorneys: this.onlyAttorneys,
        teams: this.teams,
        userTypes: this.userTypes?.length ? this.userTypes : undefined,
      }).reduce((arr, u) => {
        if (this.allowedUsers && !this.allowedUsers.includes(u.value)) { return arr }
        arr.push({
          ...u,
          text: `${u.displayName} (${u.login})`,
        })
        return arr
      }, [])
      userList = sortByKeyCaseInsensitive(userList, 'displayName')
      if ((this.extraOptions || []).length) {
        userList.splice(0, 0, ...this.extraOptions.map(opt => {
          return {
            ...opt,
            displayName: opt.text,
          }
        }))
      }
      return userList
    },

    dropdownMode () {
      return this.mode === 'dropdown'
    },

    menuMode () {
      return this.mode === 'menu'
    },

    selectedUsers: {
      get () {
        return this.lazyUsers || []
      },

      set (value, oldValue) {
        if (value === oldValue) return

        this.lazyUsers = value
        this.$emit('change', value)
      },
    },

    ...mapGetters([
      'usersOptions',
    ]),
  },

  watch: {
    users (value, oldValue) {
      this.lazyUsers = value
    },
  },

  created () {
    this.lazyUsers = this.users || []
    const usersOptions = this.usersOptions()
    if (!usersOptions || usersOptions.length === 0) { this.getUsersModel() }
  },

  methods: {
    focus () {
      if (this.$refs.userComponent.focus) this.$refs.userComponent.focus()
    },

    removeUser (user) {
      if (!Array.isArray(this.lazyUsers)) { return }
      const idx = this.lazyUsers.findIndex(u => u === user.value)
      if (idx >= 0) { this.selectedUsers.splice(idx, 1) }
    },

    validate () {
      if (this.$refs.userComponent?.validate) this.$refs.userComponent.validate()
    },

    ...mapActions([
      'getUsersModel',
    ]),
  },
}
</script>

<style scoped>
.user-details {
  *::after {
    content: "\2022";
    display: inline;
    margin-right: 4px !important;
    margin-left: 4px !important;
  }
  :last-child::after {
    display: none;
  }
}
</style>
